import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { TRAVELER_SELECTION_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import StickyEffect from '@/components/StickyEffect'
import fireMaxRoomsEvent from '@/components/TravelerSelection/ga4'
import { SearchFormContext } from '@/context/SearchFormContext'
import type { GA4PageNameType } from '@/ga4types'
import useSeti from '@/hooks/useSeti'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import pclnExperimentation from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import type { DateRange, ITravelerInfo } from '@pcln/horizon'
import { Button, cn, DatePicker, TRANSFORMER_MAX_MOBILE_WIDTH_PX, TravelerSelection } from '@pcln/horizon'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMediaQuery } from 'react-responsive'
import Sticky from 'react-stickynode'
import { groupDealsURL } from '../SearchForm/utils'
import {
  INFANT_CAPACITY_LIMIT,
  isInfantLimitReachedPackages,
  searchFormSchema,
} from '../SearchForm/validation'
import type {
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
  SelectedTopFilters,
  SEM_SEARCH_TYPE,
} from '../types'
import FormTypeRadios from './HorizonFormTypeRadio'
import { fireBundleAndSaveEvent, fireMultiHotelRadioClick } from '../ga4'
import { getFormContainerClass, getGridContainerClass, getInputClass } from './HorizonHotelForm.variants'
import MultiHotelForm from './HorizonMultiHotelForm/HorizonMultiHotelForm'
import HorizonSelectedFilterChipBar from './HorizonSelectedFilterChipBar'
import useHotelFormInitialValues from './useHotelFormInitialValues'
import useHotelFormSubmitValues from './useHotelFormSubmitValues'

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) => void
  // promoDeal?: PromoDealType
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  showHotelTypeButtons?: boolean
  pageName?: 'homepage' | 'landing'
  useCompactLayout?: boolean
}

export default function HorizonHotelForm({
  onSubmit,
  setLocation = () => null,
  showHotelTypeButtons = false,
  pageName = 'homepage',
  useCompactLayout = false,
}: FormType) {
  const [hotelType, setHotelType] = useState('single')
  const [addFlights, setAddFlights] = useState(false)
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const { registerTabElement, focusNextElement } = useTabGroup()
  const { updateFormValues } = useContext(SearchFormContext)
  const { initialValues, isMobile, cguid, SEMSearch, SEMSEARCH } = useHotelFormInitialValues()
  const useV2Typeahead = useFeature('siteExperience.useV2Typeahead') ?? false
  const typeAheadEndpointHotels = useV2Typeahead ? 'hotelsV2' : 'hotels'

  const isMobileComponent = useMediaQuery({ maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX })
  const {
    clearErrors,
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<HOTEL_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange',
  })

  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
  } = useHotelFormSubmitValues({
    control,
    watch,
    clearErrors,
    trigger,
    initialValues,
    setLocation,
    updateFormValues,
  })

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    flights: false,
    car: false,
  })

  const enableStickyEffect = !isMobile && pageName === 'homepage' && hotelType === 'single'

  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      // Only allow sticky for single hotel forms
      if (enableStickyEffect) {
        setIsStickyInEffect(true)
      }
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }
  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const currentDate = new Date()
  const [defaultMonth, setDefaultMonth] = useState<Date>(currentDate)

  const onCalendarChange = useCallback(
    (dates: DateRange): void => {
      if (dates?.from) {
        const startDate = format(dates.from, 'yyyyMMdd')
        setValue('startDate', startDate, { shouldDirty: true })
        setDefaultMonth(dates.from)
      }

      if (dates?.to) {
        const endDate = format(dates.to, 'yyyyMMdd')
        setValue('endDate', endDate, { shouldDirty: true })
      }

      if (dates?.from && dates?.to === undefined) {
        setValue('endDate', '', { shouldDirty: true })
        setMaxSelectableDate(addDays(dates.from, 28))
      } else {
        setMaxSelectableDate(undefined)
      }
    },
    [setValue],
  )

  const isPartnersURL = useSeti('HP_LP_HOTELS_TEN_ROOMS', false) === 'VARIANT'

  const hotelPlannerLink = groupDealsURL({
    location: watchEndLocation ? watchEndLocation.itemName : '',
    startDate: watchStartDate,
    endDate: watchEndDate,
    isPartnersURL,
  })
  const hotelPlannerLinkAttrsProp = {
    href: hotelPlannerLink,
    target: '_blank',
  }

  const multiHotelInitialData = useMemo(
    () => ({
      endLocation: watchEndLocation,
      startDate: watchStartDate,
      endDate: watchEndDate,
      travelers: watchTravelers,
    }),
    [watchEndLocation, watchStartDate, watchEndDate, watchTravelers],
  )

  const [selectedFilterChipData, setSelectedFilterChipData] = useState<SelectedTopFilters>(() => ({
    amenities: new Set(initialValues.selectedTopFilters.amenities),
    hotelStarLevel: new Set(initialValues.selectedTopFilters.hotelStarLevel),
    guestRating: new Set(initialValues.selectedTopFilters.guestRating),
    propertyTypes: new Set(initialValues.selectedTopFilters.propertyTypes),
  }))

  const [geolocationError, setGeolocationError] = useState<string | null>(null)

  const onItemSelect = useCallback(
    (item: LOCATION_SEARCH_TYPE | null, searchKey: 'startLocation' | 'endLocation') => {
      setValue(searchKey, item)

      // Clear geolocation error when user selects a valid location
      if (searchKey === 'endLocation' && item) {
        setGeolocationError(null)
      }

      if (!isMobile && !!item && typeof focusNextElement === 'function') {
        focusNextElement(true)
      } else if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [isMobile, setValue, focusNextElement],
  )

  const hasSelectedFilters = Object.values(selectedFilterChipData).some((value) => value.size > 0)

  const showSelectedFilters = watchTripType === 'STAY' && hasSelectedFilters

  const isHomepage = pageName === 'homepage'

  const handleSubmitHelper = handleSubmit((data: HOTEL_FORM_STATE_TYPE) => {
    if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
      if (isHomepage) {
        window.PCLN.pclnEventDispatcher.dispatch('FIND YOUR HOTEL')
      }
      const bookingInfo = {
        cityName: data.endLocation?.cityName,
        cityId: data.endLocation?.cityID,
        checkInDate: data.startDate,
        checkOutDate: data.endDate,
      }
      window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_HOTEL_DATA', bookingInfo)
    }
    let userLatitude: number | undefined
    let userLongitude: number | undefined
    if (data.endLocation?.type === 'GEO' && data.endLocation?.lat && data.endLocation?.lon) {
      userLatitude = data.endLocation.lat
      userLongitude = data.endLocation.lon
    }
    const formValues = {
      ...data,
      ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
      ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
      mapView: false,
      selectedTopFilters: selectedFilterChipData,
      useCurrentLocation: data.endLocation?.type === 'GEO',
      ...(userLatitude &&
        userLongitude && {
          userLatitude,
          userLongitude,
        }),
    }
    onSubmit(formValues, pageName)
    logSearchFormAnalytics(cguid, pageName, 'hotels', isMobile)
  })

  const handleTravelerChange = (travelerInfo: ITravelerInfo): void => {
    setValue('travelers', travelerInfo as TravelerState)
  }

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections((prev) => ({
        ...prev,
        [optionValue]: checked,
      }))
      setValue('tripType', updatedTripType)
      if (checked) {
        try {
          fireBundleAndSaveEvent(updatedTripType)
        } catch {
          // GA4TripTypeMapper may throw on unexpected trip type combinations
        }
      }
      if (optionValue === 'FLY') {
        setAddFlights(checked)
      }
    },
    [setValue],
  )

  const formClasses = getFormContainerClass(
    addFlights,
    isStickyInEffect,
    showSelectedFilters,
    useCompactLayout,
  )
  const gridContainerClass = getGridContainerClass(
    addFlights,
    isStickyInEffect,
    showSelectedFilters,
    useCompactLayout,
  )
  const numberOfMonthsDisplay = watchTripType === 'STAY' ? 13 : 12

  return (
    <div className='w-auto'>
      {showHotelTypeButtons && (
        <FormTypeRadios
          formType={hotelType}
          onChange={(selectedFormName) => {
            setHotelType(selectedFormName)
            fireMultiHotelRadioClick(pageName)
          }}
        />
      )}
      <StickyEffect
        enabled={enableStickyEffect}
        isStickyInEffect={isStickyInEffect}
        handleStickyEffectStateChange={handleStickyEffectStateChange}
        innerZ={isMobile ? 0 : 3}
      >
        {hotelType === 'multi' && showHotelTypeButtons ? (
          <MultiHotelForm
            pageName={pageName}
            initialData={multiHotelInitialData}
            useCompactLayout={useCompactLayout}
          />
        ) : (
          <div className={formClasses}>
            <form className='w-auto' aria-label='horizon-search-form' onSubmit={handleSubmitHelper}>
              <div className={gridContainerClass}>
                {addFlights && (
                  <div className={getInputClass('flight')}>
                    <AutocompleteWrapper
                      label='Where from?'
                      isMobile={isMobile}
                      errorMessage={errors.startLocation?.message}
                      searchKey='startLocation'
                      typeAheadEndpoint='packagesOrigin'
                      initialSelectedItem={watchStartLocation as LOCATION_SEARCH_TYPE | undefined}
                      onItemSelect={(itemSelected) => {
                        onItemSelect(itemSelected, 'startLocation')
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                      }}
                    />
                  </div>
                )}
                <div className={getInputClass('location')}>
                  <AutocompleteWrapper
                    label='Where to?'
                    isMobile={isMobile}
                    showCurrentLocation
                    errorMessage={errors.endLocation?.message ?? geolocationError ?? undefined}
                    searchKey='endLocation'
                    initialSelectedItem={watchEndLocation as LOCATION_SEARCH_TYPE | undefined}
                    typeAheadEndpoint={typeAheadEndpointHotels}
                    onItemSelect={(itemSelected) => {
                      onItemSelect(itemSelected, 'endLocation')
                    }}
                    onGeolocationError={setGeolocationError}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                    selectedFilterChipData={selectedFilterChipData}
                    setSelectedFilterChipData={setSelectedFilterChipData}
                    showFilterChips={watchTripType === 'STAY'}
                  />
                </div>

                <div className={getInputClass('datePicker')}>
                  <DatePicker
                    inputRef={registerTabElement}
                    errorMessage={errors.startDate?.message || errors.endDate?.message}
                    className='w-full'
                    title='Hotels: Check-in - Check-out'
                    label='Check-in - Check-out'
                    mode='range'
                    numberOfMonths={isMobileComponent ? numberOfMonthsDisplay : 2}
                    handleCalendarChange={onCalendarChange}
                    selected={{
                      from: currentDate,
                      to: addDays(currentDate, 1),
                    }}
                    disabled={{
                      before: currentDate,
                      after: maxSelectableDate,
                    }}
                    startMonth={currentDate}
                    endMonth={addMonths(currentDate, numberOfMonthsDisplay - 1)}
                    defaultMonth={defaultMonth}
                    min={1}
                  />
                </div>
                <div className={getInputClass('travelers')}>
                  <TravelerSelection
                    isHotel={!watchTripType.includes('FLY')}
                    initialValues={initialValues.travelers}
                    roomsEnabled
                    maxRooms={watchTripType === 'STAY' ? 9 : 4}
                    maxChildren={watchTripType === 'STAY' ? 8 : undefined}
                    maxTravelers={watchTripType === 'STAY' ? 36 : 8}
                    fireMaxRoomsOnClick={() => {
                      pclnExperimentation.fireImpression('HP_LP_HOTELS_TEN_ROOMS')
                      window.open(hotelPlannerLinkAttrsProp.href, '_blank')
                      fireMaxRoomsEvent(pageName, 'hotel')
                    }}
                    ageSelectEnabled
                    tooltipErrorMessageId='travelerSelectionError'
                    errorMessage={
                      errors?.travelers?.children?.message ||
                      errors?.travelers?.rooms?.message ||
                      (isInfantLimitReachedPackages(
                        watchTravelers?.adults,
                        watchTravelers?.childrenAges,
                        watchTripType,
                      ) &&
                        INFANT_CAPACITY_LIMIT) ||
                      ''
                    }
                    onChange={(values: ITravelerInfo) => {
                      handleTravelerChange(values)
                    }}
                    collisionAvoidance={TRAVELER_SELECTION_COLLISION_AVOIDANCE}
                  />
                </div>
                {showSelectedFilters && (
                  <div className={getInputClass('selectedFilters')}>
                    <HorizonSelectedFilterChipBar
                      selectedFilterChipData={selectedFilterChipData}
                      setSelectedFilterChipData={setSelectedFilterChipData}
                    />
                  </div>
                )}
                <div className={getInputClass('packageDeals', isStickyInEffect)}>
                  <HorizonBundleAndSave
                    checkedValues={bundleSelections}
                    onSelectionChange={handleBundleSelectionChange}
                    tripType={watchTripType}
                    productType='hotels'
                  />
                </div>
                <Button
                  buttonType='submit'
                  type='primary'
                  size='lg'
                  className={cn(getInputClass('submit'), isMobile && 'fixed right-4 bottom-4 left-4')}
                >
                  Find Your Hotel
                </Button>
              </div>
            </form>
          </div>
        )}
      </StickyEffect>
    </div>
  )
}
